import { Button, TextField } from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStoreDispatch, useStoreSelector } from "../../../../../hooks/StoreHooks";
import { getCurrentUser } from "../../../../core/common_functions";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Formik, Form } from "formik";
import { usePergolaConfigurationBuilderActions } from "../../../../../store/reducers/pergola_configuration_builder";
import { Fioriera, Fioriere } from "../../../../models/pergola_configuration";
import { useConfigurationsServicePergola } from "../../../../services/pergola_service";
import { LatoInstallation } from "./components/forms/Pergola_type";

export const FiorierePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useStoreDispatch();
  const { base, pergolaConfigurationBuilder } = useStoreSelector(store => store);
  const dispatcher = useStoreDispatch();
  const { setStepActive, setStepComplete, removeFiorieraTipo} = usePergolaConfigurationBuilderActions();
  const cg = useConfigurationsServicePergola();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const currentUser = getCurrentUser(base);
  // Recupera le fioriere dallo stato del builder
  const fioriere = pergolaConfigurationBuilder.fioriere || [];

  // Calcola automaticamente le quantità
  const quantita500 = useMemo(() => {
    return fioriere.filter(f => f.tipo === Fioriera.fioriera_500x500mm).length;
  }, [fioriere]);

  const quantitaL = useMemo(() => {
    return fioriere.filter(f => f.tipo === Fioriera.fioriera_807).length;
  }, [fioriere]);

  useEffect(() => {
    if (!id || isNaN(Number(id)) || !pergolaConfigurationBuilder.configuration) {
      navigate("/configurations");
    } else {
      dispatcher(setStepActive({ stepSlug: "fioriere", isActive: true }));
    }
  }, [id, pergolaConfigurationBuilder.configuration, dispatcher, navigate]);

  const initialValues = {
    fioriera_500: quantita500,
    fioriera_L: quantitaL,
  };

  const submit = async () => {
    setIsSubmit(true);
    try {
      const data : {fioriere: Fioriere[]} = {fioriere: pergolaConfigurationBuilder.fioriere};
      if (pergolaConfigurationBuilder.configuration && data) {
        await cg.updateFioriere(
          currentUser,
          pergolaConfigurationBuilder.configuration.configurationId,
          data
        );
      }
      dispatcher(setStepComplete({ stepSlug: "fioriere", isComplete: true }));
      const lati = pergolaConfigurationBuilder.configuration?.dimensioni.lati ?? [];
      const TOTAL_LATI = 4;
      const tuttiAddossati =
        (lati?.length ?? 0) === TOTAL_LATI &&
        lati!.every(l => l.tipo === LatoInstallation.addossato);
      if (tuttiAddossati) {
        dispatcher(setStepActive({ stepSlug: "accessori-vari", isActive: true }));
        navigate(`/pergola-configurations/${id}/accessori-vari`);
      } else {
        dispatcher(setStepActive({ stepSlug: "estetica", isActive: true }));
        navigate(`/pergola-configurations/${id}/estetica`);
      }
    } catch (e: any) {
      console.error(e);
    } finally {
      setIsSubmit(false);
    }
  };

  return (
    <>
      <h5 className="text-primary mb-4">Fioriere</h5>

      <Formik initialValues={initialValues} onSubmit={submit} enableReinitialize>
        {({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            {/* Fioriera 500x500 */}
            <div className="d-flex align-items-center justify-content-between mb-3 p-3 border rounded">
              <div className="flex-grow-1 pe-3">
                <small className="text-muted d-block">Fioriera dimensioni 500x500mm</small>
                <span className="fw-medium">
                  Fioriera dimensioni 500x500mm con struttura in tubolari in alluminio con lamiera interna ed esterna in alluminio spessore 1mm
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <TextField
                  size="small"
                  type="number"
                  name="fioriera_500"
                  value={quantita500}
                  inputProps={{ min: 0, readOnly: true }}
                  sx={{ width: 80 }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => {
                    dispatch(removeFiorieraTipo({tipo: Fioriera.fioriera_500x500mm}));
                  }}
                >
                  RESETTA
                </Button>
              </div>
            </div>

            {/* Fioriera ad L 807,5x807,5 */}
            <div className="d-flex align-items-center justify-content-between mb-4 p-3 border rounded">
              <div className="flex-grow-1 pe-3">
                <small className="text-muted d-block">Fioriera ad “L” dimensioni 807,5x807,5mm</small>
                <span className="fw-medium">
                  Fioriera ad “L” dimensioni 807,5x807,5mm con struttura in tubolari in alluminio con lamiera interna ed esterna in alluminio spessore 1mm
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <TextField
                  size="small"
                  type="number"
                  name="fioriera_L"
                  value={quantitaL}
                  inputProps={{ min: 0, readOnly: true }}
                  sx={{ width: 80 }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => {
                        dispatch(removeFiorieraTipo({tipo: Fioriera.fioriera_807}));
                    }}
                >
                  RESETTA
                </Button>
              </div>
            </div>

            {/* Istruzione centrale */}
            <p className="py-3 text-center">
              <ArrowBackOutlinedIcon color="primary" />
              <strong> Clicca sul pilastro per posizionare/eliminare la fioriera</strong>
            </p>

            {/* Pulsanti navigazione */}
            <div className="d-flex flex-row-reverse justify-content-between mt-4">
              <Button
                disabled={isSubmit}
                onClick={submit}
                variant="contained"
                className="ps-3 pe-1"
              >
                <span>Avanti</span>
                <NavigateNextIcon />
              </Button>

              <Button
                variant="contained"
                color="secondary"
                className="ps-1 pe-3"
                onClick={() => {
                  dispatcher(setStepActive({ stepSlug: "illuminazioni", isActive: true }));
                  dispatcher(setStepComplete({ stepSlug: "fioriere", isComplete: true }));
                  navigate(`/pergola-configurations/${id}/illuminazioni`);
                }}
              >
                <NavigateBeforeIcon />
                <span>Indietro</span>
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};